import * as React from "react";
import { api } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { Coins, AlertTriangle } from "lucide-react";

/**
 * BoardCostHeader — compact rollup pill that lives at the top of the
 * BoardView.  Fetches ``/boards/{id}/cost`` and shows logged
 * cost + budget + variance chip.
 *
 * Design goals:
 *   - Never opinionated when there's no budget (just shows cost).
 *   - Amber "over budget" state when cost > budget.
 *   - Mono numerals throughout — this is the number people trust.
 *   - Cost = internal labour valued at each entry's worked-date HR
 *     rate.  Same figure the pay engine bills against.
 */
export default function BoardCostHeader({ companyId, boardId, refreshKey = 0 }) {
  const [cost, setCost] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/boards/${boardId}/cost`);
        if (alive) setCost(data);
      } catch (_) {
        // Silent — the header should never block the board load.
      }
    })();
    return () => { alive = false; };
  }, [companyId, boardId, refreshKey]);

  if (!cost) return null;
  if (cost.total_minutes === 0 && !cost.budget_cents) return null;

  const over = cost.over_budget;
  const varianceLabel = cost.variance_cents === null
    ? null
    : (cost.variance_cents >= 0
        ? `£${(cost.variance_cents / 100).toFixed(2)} under`
        : `£${(Math.abs(cost.variance_cents) / 100).toFixed(2)} over`);

  return (
    <div className={`flex items-center gap-3 px-3 py-1.5 rounded-md border ${over ? "border-amber-300 bg-amber-50" : "border-brand-rule bg-brand-canvas-2"}`}
         data-testid="board-cost-header">
      <Coins size={14} className={over ? "text-amber-600" : "text-brand-violet"} />
      <div className="flex items-baseline gap-1.5">
        <span className="micro-label">Logged</span>
        <span className="mono-num text-[13px] font-semibold text-brand-ink"
              data-testid="board-cost-logged">
          {fmtCents(cost.total_cost_cents)}
        </span>
        <span className="text-[10.5px] text-brand-muted mono-num ml-1">
          {(cost.total_minutes / 60).toFixed(1)}h
        </span>
      </div>
      {cost.budget_cents != null && (
        <>
          <div className="h-4 w-px bg-brand-rule" />
          <div className="flex items-baseline gap-1.5">
            <span className="micro-label">Budget</span>
            <span className="mono-num text-[12.5px] text-brand-ink"
                  data-testid="board-cost-budget">
              {fmtCents(cost.budget_cents)}
            </span>
          </div>
          <div className="h-4 w-px bg-brand-rule" />
          <div className={`flex items-center gap-1 text-[11px] font-semibold ${over ? "text-amber-800" : "text-emerald-800"}`}
               data-testid="board-cost-variance">
            {over && <AlertTriangle size={11} />}
            <span className="mono-num">{varianceLabel}</span>
          </div>
        </>
      )}
      {cost.missing_rate_minutes > 0 && (
        <span className="text-[10.5px] text-brand-muted mono-num"
              title="Time logged for contributors with no HR rate on that day. Not costed.">
          · {cost.missing_rate_minutes}m no-rate
        </span>
      )}
      <span className="ml-auto text-[9.5px] text-brand-faint hidden lg:inline">
        Internal labour at HR rates
      </span>
    </div>
  );
}
